import { useMutation, useQueryClient } from '@tanstack/react-query'
import { sessionsApi } from '@api/endpoints/sessions'
import { useSessionStore } from '../stores/sessionStore'
import { sessionKeys } from './useSession'
import { SubmitInputRequest, SessionInputType } from '@api/types/session'

/**
 * Hook for submitting input to challenge nodes
 */
export function useSessionInput(sessionId: string | null) {
  const queryClient = useQueryClient()
  const store = useSessionStore()

  const mutation = useMutation({
    mutationFn: async (data: SubmitInputRequest) => {
      if (!sessionId) throw new Error('No active session')
      store.setSubmitting(true)
      store.setError(null)
      const result = await sessionsApi.submitInput(sessionId, data)
      return result
    },
    onSuccess: (result) => {
      store.setSubmitting(false)
      if (result.data) {
        // Update store with new session state
        store.updateSessionState(result.data.session)
        // Add any new journal entries
        if (result.data.newJournalEntries?.length > 0) {
          store.addJournalEntries(result.data.newJournalEntries)
        }
        // Capture completion summary if present (terminal state reached)
        if (result.data.completionSummary) {
          store.setCompletionSummary(result.data.completionSummary)
        }
      }
      if (sessionId) {
        queryClient.invalidateQueries({ queryKey: sessionKeys.state(sessionId) })
        // Also invalidate journal to fetch any new entries from the server
        queryClient.invalidateQueries({ queryKey: sessionKeys.journal(sessionId) })
      }
    },
    onError: (error) => {
      store.setSubmitting(false)
      store.setError(error instanceof Error ? error.message : 'Failed to submit input')
    },
  })

  // Convenience methods for different input types
  const submitAnswer = (nodeId: string, value: string) => {
    mutation.mutate({
      nodeId,
      inputType: 'QuestionAnswer' as SessionInputType,
      value,
    })
  }

  const submitNumpad = (nodeId: string, value: string) => {
    mutation.mutate({
      nodeId,
      inputType: 'Numpad' as SessionInputType,
      value,
    })
  }

  const submitQrCode = (nodeId: string, value: string) => {
    mutation.mutate({
      nodeId,
      inputType: 'QRCode' as SessionInputType,
      value,
    })
  }

  const submitCodex = (nodeId: string, value: string) => {
    mutation.mutate({
      nodeId,
      inputType: 'Codex' as SessionInputType,
      value,
    })
  }

  // Skip current challenge (if allowed)
  const skipChallenge = (nodeId: string, inputType: SessionInputType) => {
    mutation.mutate({
      nodeId,
      inputType,
      value: '__SKIP__', // Special value indicating skip
    })
  }

  // Cancel current challenge (if allowed)
  const cancelChallenge = (nodeId: string, inputType: SessionInputType) => {
    mutation.mutate({
      nodeId,
      inputType,
      value: '__CANCEL__', // Special value indicating cancel
    })
  }

  return {
    submitInput: mutation.mutate,
    submitAnswer,
    submitNumpad,
    submitQrCode,
    submitCodex,
    skipChallenge,
    cancelChallenge,
    isSubmitting: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    lastResult: mutation.data?.data,
  }
}
